/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import { SpinnerBlock } from "../common/Spinner";
import * as authSelectors from "../auth/authSelectors";
import { routes } from "../../appRoutingHelpers";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import * as diacritics from "diacritics";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { ButtonInternalLink } from "../../components/NewButtons";
import { useGetCountries, useGetVouchers } from "../../service/api";
import { css } from "@emotion/react";
import { breakpoints } from "../../components/breakpoints";
import { SearchTextFieldWithKeyboardInteraction } from "../../components/forms/fields/SearchTextFieldWithKeyboardInteraction";
import VoucherTypeListItem from "./VoucherTypeListItem";
import { useHandleFetchError } from "../../error-handler";
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { cardBodyAdditionalStyling } from "../shop/SalesChannelsList";
import { EmptyState } from "../../components/EmptyState";
import { firstTableCellStyle } from "../../style-util";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";
import { Divider } from "../../components/Divider";

const textFieldStyling = css`
  width: 350px;
  ${breakpoints.tablet} {
    width: 100%;
  }
`;

const headerWrapperStyling = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const VoucherTypeList = () => {
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  const {
    searchValue,
    setSearchValue,
    removeSearchValue,
  } = useSearchParamsManipulation();

  const {
    data,
    isLoading: isLoadingVoucherTypeList,
    error: voucherTypesError,
  } = useGetVouchers(organization);

  const {
    data: countries,
    isLoading: isLoadingCountries,
    error: getCountriesError,
  } = useGetCountries();

  const returnFilteredVoucherList = () => {
    const sanitizedSearchValue = diacritics.remove(
      searchValue?.toLowerCase().replace(/\W+/g, "") || ""
    );
    return (data || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter((v) =>
        diacritics
          .remove(v.name.toLowerCase().replace(/\W+/g, ""))
          .includes(sanitizedSearchValue)
      );
  };
  useHandleFetchError(voucherTypesError);
  useHandleFetchError(getCountriesError);

  if (isLoadingVoucherTypeList || isLoadingCountries || !countries || !data) {
    return <SpinnerBlock />;
  }

  return (
    <PageWrapper>
      <CardContainer>
        <PageHeader
          title="Voucher templates"
          subtitle="Manage all of your voucher templates"
        >
          <div css={headerWrapperStyling}>
            <div css={textFieldStyling}>
              <SearchTextFieldWithKeyboardInteraction
                searchValue={searchValue || ""}
                setSearchValue={(value) => {
                  if (value === "") {
                    removeSearchValue();
                  } else {
                    setSearchValue(value);
                  }
                }}
                placeholder="Start typing to search"
              />
            </div>
            <ButtonInternalLink
              height="100%"
              to={routes.voucherTypeNew(organization)}
            >
              Add new
            </ButtonInternalLink>
          </div>
        </PageHeader>
        {returnFilteredVoucherList()?.length === 0 || !data ? (
          <>
            <Divider />
            <CardBody additionalStyles={cardBodyAdditionalStyling}>
              <EmptyState
                title={
                  data?.length === 0
                    ? "No voucher templates created yet"
                    : "No results found"
                }
                subtitle={
                  data?.length === 0
                    ? "Create your first voucher template by pressing on create voucher template in top right corner"
                    : "Try to search with different keywords"
                }
              />
            </CardBody>
          </>
        ) : (
          <Table>
            <thead>
              <TableRowHeader>
                <TableCellHeaderText css={firstTableCellStyle}>
                  Admin name
                </TableCellHeaderText>
                <TableCellHeaderText>Country</TableCellHeaderText>
                <TableCellHeaderText>Discount type</TableCellHeaderText>
                <TableCellHeaderText>Discount value</TableCellHeaderText>
                <TableCellHeaderText>
                  Vouchers valid for (from user claims voucher)
                </TableCellHeaderText>
                <TableCellHeaderText>Expires at</TableCellHeaderText>
              </TableRowHeader>
            </thead>
            <tbody>
              {returnFilteredVoucherList().map((voucherType) => {
                return (
                  <VoucherTypeListItem
                    key={voucherType.id}
                    voucherType={voucherType}
                    countries={countries}
                    href={routes.voucherTypeEdit(organization, voucherType.id)}
                  />
                );
              })}
            </tbody>
          </Table>
        )}
      </CardContainer>
    </PageWrapper>
  );
};

export default VoucherTypeList;
